import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { collection, doc, onSnapshot } from 'firebase/firestore'
import { db } from './firebase'
import { useAuth } from './auth'
import {
  FREE_PLAN,
  FREE_PLAN_ID,
  DEFAULT_PLANS,
  defaultPlan,
  effectivePlanId,
  mergePlan,
} from './plans'
import type { FeatureKey, Plan, PlanOverride, SubscriptionRecord } from './plans'
import type { Tour, WithId } from '../types/models'

/* ============================================================================
   What this account is allowed to do.

   The browser half of `Encore/Subscriptions/Entitlements.swift`, answering the
   same questions from the same documents. Every answer comes from a `Plan`
   record — never a tier name, never a rank comparison.

   Two live listeners, both failing soft to the free plan. A network partition
   must degrade someone to free, never lock them out.
   ============================================================================ */

interface EntitlementsValue {
  plan: Plan
  subscription: SubscriptionRecord | null
  /** An admin comp, if one is live. Surfaced so the account screen can say
   *  "Pro, comped until March" rather than just "Pro". */
  override: PlanOverride | null
  /** Every plan worth showing, rank order. Firestore wins; defaults fill gaps. */
  allPlans: Plan[]
  /**
   * False until both listeners have reported. Gate any "you're on the free
   * plan, upgrade?" prompt on this — showing it during the first read tells
   * paying customers they haven't paid.
   */
  loaded: boolean

  permits: (feature: FeatureKey) => boolean
  canCreateTour: (activeCount: number) => boolean
  /** Viewers are never limited on any plan, so there is deliberately no
   *  matching viewer method to call. */
  canAddEditor: (currentCount: number) => boolean
  canAddArtist: (currentCount: number) => boolean
  tourLimitMessage: string
}

const EntitlementsContext = createContext<EntitlementsValue | null>(null)

export function EntitlementsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [catalogue, setCatalogue] = useState<Record<string, Plan>>({})
  const [subscription, setSubscription] = useState<SubscriptionRecord | null>(null)
  const [override, setOverride] = useState<PlanOverride | null>(null)
  const [loadedPlans, setLoadedPlans] = useState(false)
  const [loadedAccount, setLoadedAccount] = useState(false)

  useEffect(() => {
    return onSnapshot(
      collection(db, 'plans'),
      (snapshot) => {
        const next: Record<string, Plan> = {}
        snapshot.docs.forEach((document) => {
          next[document.id] = mergePlan(document.id, document.data())
        })
        setCatalogue(next)
        setLoadedPlans(true)
      },
      () => {
        // Stale pricing beats no pricing. The built-in defaults stand.
        setLoadedPlans(true)
      },
    )
  }, [])

  useEffect(() => {
    if (!user) {
      setSubscription(null)
      setOverride(null)
      setLoadedAccount(true)
      return
    }
    return onSnapshot(
      doc(db, 'users', user.uid),
      (snapshot) => {
        setSubscription((snapshot.data()?.subscription as SubscriptionRecord) ?? null)
        setOverride((snapshot.data()?.planOverride as PlanOverride) ?? null)
        setLoadedAccount(true)
      },
      () => setLoadedAccount(true),
    )
  }, [user])

  const value = useMemo<EntitlementsValue>(() => {
    /** Firestore first, built-in default second, free last. */
    const resolve = (planId: string): Plan =>
      catalogue[planId] ?? defaultPlan(planId) ?? FREE_PLAN

    const plan = resolve(effectivePlanId(subscription, override))

    const ids = new Set([...Object.keys(catalogue), ...DEFAULT_PLANS.map((entry) => entry.id)])
    const allPlans = [...ids].map(resolve).sort((a, b) => a.rank - b.rank)

    return {
      plan,
      subscription,
      override,
      allPlans,
      loaded: loadedPlans && loadedAccount,

      permits: (feature) => plan.features.includes(feature),
      canCreateTour: (activeCount) =>
        plan.maxActiveTours === null || activeCount < plan.maxActiveTours,
      canAddEditor: (currentCount) =>
        plan.maxEditorSeats === null || currentCount < plan.maxEditorSeats,
      canAddArtist: (currentCount) =>
        plan.maxArtists === null || currentCount < plan.maxArtists,

      tourLimitMessage:
        plan.maxActiveTours === null
          ? ''
          : plan.maxActiveTours === 1
            ? `${plan.displayName} covers one tour at a time. Finished tours don't count — upgrade to run more at once.`
            : `${plan.displayName} covers ${plan.maxActiveTours} tours at a time. Upgrade to run more at once.`,
    }
  }, [catalogue, subscription, override, loadedPlans, loadedAccount])

  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>
}

export function useEntitlements(): EntitlementsValue {
  const value = useContext(EntitlementsContext)
  if (!value) throw new Error('useEntitlements must be used inside EntitlementsProvider')
  return value
}

export { FREE_PLAN_ID }

/* --------------------------------------------------------------- what counts */

/**
 * Whether a tour consumes plan allowance.
 *
 * Owned, because being on someone else's crew shouldn't cost you a slot — and
 * unfinished, because a tour that ended must never be the reason someone can't
 * start the next one. Past tours stay fully readable and exportable.
 *
 * A tour with no end date counts, on the grounds that an open-ended run is the
 * most active thing there is. Mirrors `Tour.countsTowardActiveLimit` in Swift.
 */
export function countsTowardActiveLimit(tour: Tour): boolean {
  const end = tour.endDate?.toDate?.()
  if (!end) return true
  const midnight = new Date()
  midnight.setHours(0, 0, 0, 0)
  return end >= midnight
}

export function activeTourCount(tours: Array<WithId<Tour>>, ownerId: string): number {
  return tours.filter((tour) => tour.ownerId === ownerId && countsTowardActiveLimit(tour)).length
}

/**
 * Whether another tour may be started, and what to say if not.
 *
 * `allowed` stays true until the entitlement layer has actually loaded — a
 * blocked button during the first read would stop a paying customer creating
 * a tour for no reason they can see.
 */
export function useTourAllowance(tours: Array<WithId<Tour>>, ownerId: string | undefined) {
  const { canCreateTour, tourLimitMessage, plan, loaded } = useEntitlements()
  const used = ownerId ? activeTourCount(tours, ownerId) : 0

  return {
    used,
    limit: plan.maxActiveTours,
    allowed: !loaded || canCreateTour(used),
    message: tourLimitMessage,
  }
}
